package com.castly.admin.external

import org.w3c.dom.Element

/**
 * Kotlin/JS external declarations for SortableJS library
 * Provides drag-and-drop reordering functionality with touch support
 *
 * Usage:
 * ```
 * val sortable = Sortable.create(containerElement, SortableOptions(
 *   animation = 150,
 *   delayOnTouchOnly = true,
 *   touchStartThreshold = 10
 * ))
 * ```
 */

// Webpack wraps CommonJS modules as { default: <export>, ... }.
// @JsModule gives us that namespace object; .default is the actual Sortable constructor.
@JsModule("sortablejs")
@JsNonModule
private external object _sortableModule

object Sortable {
    fun create(element: Element, options: SortableOptions): SortableInstance {
        return _sortableModule.asDynamic().default.create(element, options).unsafeCast<SortableInstance>()
    }
}

external interface SortableOptions {
    var animation: Int?
    var delay: Int?
    var delayOnTouchOnly: Boolean?
    var touchStartThreshold: Int?
    var ghostClass: String?
    var dragClass: String?
    var handle: String?
    var onEnd: ((event: SortableEvent) -> Unit)?
    var onStart: ((event: SortableEvent) -> Unit)?
    var onMove: ((event: SortableEvent) -> Boolean)?
    var onUpdate: ((event: SortableEvent) -> Unit)?
}

external interface SortableEvent {
    val from: Element
    val to: Element
    val item: Element
    val clone: Element?
    val oldIndex: Int?
    val newIndex: Int?
    val oldDraggableIndex: Int?
    val newDraggableIndex: Int?
    val reason: String
    val pullMode: String?
    val originalEvent: dynamic
}

external interface SortableInstance {
    fun destroy()
    fun option(name: String, value: dynamic)
    val el: Element
}

/**
 * Helper function to create SortableOptions with fluent API
 *
 * Usage:
 * ```
 * val options = createSortableOptions {
 *   animation = 150
 *   delay = 100
 *   delayOnTouchOnly = true
 *   touchStartThreshold = 10
 *   onEnd = { event -> handleReorder(event) }
 * }
 * ```
 */
fun createSortableOptions(builder: SortableOptions.() -> Unit): SortableOptions {
    val options: SortableOptions = js("({})")
    options.builder()
    return options
}
